import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import md5 from 'md5';
import jstz from 'jstz';
import queries from './utils/queries';
import axiosDefault from './utils/axios-default';


Vue.use(Vuex);

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  if (match) {
    return match[2];
  }
  return undefined;
}

const store = new Vuex.Store({
  state: {
    token: null,
    userId: null,
    user: null,
    avatar: null,
    timezone: null,
    passwordExpired: null,
    lastActiveAt: null,
    inactiveUserTimer: null,
    announcementSeen: null,
    languageToMessage: null,

    successMessage: null,
    successMessageTrigger: null,
    warningMessage: null,
    warningMessageTrigger: null,
    infoMessage: null,
    infoMessageTrigger: null,
    errorMessage: null,
    errorMessageTrigger: null,
    error: null,
    validationErrors: null,
    errorTrigger: null,

    financingFilter: {
      country: '',
      service: '',
    },

    router: null,
  },
  mutations: {
    authUser(state, userData) {
      state.token = userData.token;
      state.userId = userData.userId;
      state.passwordExpired = userData.passwordExpired;
      state.expirationDateInUtc = userData.expirationDateInUtc;
      axios.defaults.headers.common.Authorization = `Bearer ${userData.token}`;
      state.timezone = jstz.determine()
        .name();
      const cookieExpires = new Date(0);
      cookieExpires.setUTCMilliseconds(userData.expirationDateInUtc);
      document.cookie = `jwt_token=${userData.token}; expires=${cookieExpires.toUTCString()}; path=/`;
      localStorage.setItem('userId', userData.userId);
      localStorage.setItem('expirationDateInUtc', userData.expirationDateInUtc);
    },
    storeUser(state, user) {
      state.user = user;
    },
    storeUserAvatar(state, avatarUrl) {
      state.avatar = avatarUrl;
    },
    clearAuthData(state) {
      state.token = null;
      state.userId = null;
      state.expirationDateInUtc = null;
      state.user = null;
      state.avatar = null;
      state.timezone = null;

      axios.defaults.headers.common.Authorization = '';
      document.cookie = 'jwt_token=; Max-Age=-99999999;';
      localStorage.removeItem('expirationDateInUtc');
      localStorage.removeItem('userId');
    },
    renewPassword(state) {
      state.passwordExpired = null;
    },
    setLastActiveAt(state, { lastActiveAt, newInactiveUserTimer }) {
      state.lastActiveAt = lastActiveAt;
      if (state.inactiveUserTimer) {
        clearTimeout(state.inactiveUserTimer);
      }
      state.inactiveUserTimer = newInactiveUserTimer;
    },
    storeErrorMessage(state, message) {
      state.errorMessage = message;
      state.errorMessageTrigger = new Date();
    },
    storeSuccessMessage(state, message) {
      state.successMessage = message;
      state.successMessageTrigger = new Date();
    },
    storeInfoMessage(state, message) {
      state.infoMessage = message;
      state.infoMessageTrigger = new Date();
    },
    storeWarningMessage(state, message) {
      state.warningMessage = message;
      state.warningMessageTrigger = new Date();
    },
    storeError(state, error) {
      state.error = error;
      if (error.response && error.response.data && error.response.data.errorCode === 'VALIDATION_FAILURE') {
        state.validationErrors = error.response.data.errors;
      }
      state.errorTrigger = new Date();
    },
    clearMessages(state) {
      state.errorMessage = null;
      state.successMessage = null;
      state.infoMessage = null;
      state.warningMessage = null;
      state.error = null;
      state.validationErrors = null;
    },
    setRouter(state, router) {
      state.router = router;
    },
    announcementSeen(state) {
      state.announcementSeen = true;
    },
    setLanguageToMessage(state, data) {
      state.languageToMessage = data;
    },
    updateFinancingFilter(state, { filterBy, value }) {
      state.financingFilter[filterBy] = value;
    },
  },
  actions: {
    setLogoutTimer({ dispatch }, expiresInSeconds) {
      setTimeout(() => {
        dispatch('logout');
      }, expiresInSeconds * 1000);
    },
    updateLastActiveAt({ commit, dispatch }) {
      const expiresInMinutes = 120;
      const newInactiveUserTimer = setTimeout(() => {
        dispatch('logout');
      }, expiresInMinutes * 60 * 1000);
      const lastActiveAt = Date.now();
      commit('setLastActiveAt', { lastActiveAt, newInactiveUserTimer });
    },
    login({ commit, dispatch }, credentials) {
      return axiosDefault.post(queries.login(), {
        username: credentials.username,
        password: credentials.password,
      })
        .then((res) => {
          const expirationDateInUtc = new Date(new Date().getTime() + (res.data.expiresIn * 1000))
            .getTime();
          commit('authUser', {
            token: res.data.token,
            userId: res.data.userId,
            passwordExpired: res.data.passwordExpired,
            expirationDateInUtc,
          });
          dispatch('setLogoutTimer', res.data.expiresIn);
        })
        .then(() => false)
        .catch(error => error);
    },
    tryAutoLogin({ commit, dispatch }) {
      const token = getCookie('jwt_token');
      if (!token) {
        return;
      }
      const expirationDateInUtc = localStorage.getItem('expirationDateInUtc');
      const now = new Date().getTime();
      if (now >= expirationDateInUtc) {
        return;
      }
      const userId = localStorage.getItem('userId');
      commit('authUser', {
        token,
        userId,
        expirationDateInUtc,
      });

      const expiresInSeconds = (expirationDateInUtc - new Date().getTime()) / 1000;
      dispatch('setLogoutTimer', expiresInSeconds);
    },
    logout({ commit, state }) {
      commit('clearAuthData');
      state.router.replace({ name: 'login' });
    },
    renewPassword({ commit }) {
      commit('renewPassword');
    },
    fetchUser({ commit, state, dispatch }) {
      if (!state.token) {
        return;
      }
      axios.get(queries.myUser())
        .then((res) => {
          const user = res.data;
          commit('storeUser', user);
          dispatch('fetchAvatar');
        })
        .catch(() => dispatch('logout'));
    },
    fetchAvatar({ commit, state }) {
      const emailHash = md5(state.user.email.toLowerCase());
      axiosDefault.head(queries.userProfile(emailHash))
        .then(() => {
          const avatarUrl = queries.userAvatar(emailHash);
          commit('storeUserAvatar', avatarUrl);
        })
        .catch(() => {
        });
    },
    showErrorMessage({ commit }, message) {
      commit('clearMessages');
      commit('storeErrorMessage', message);
    },
    showSuccessMessage({ commit }, message) {
      commit('clearMessages');
      commit('storeSuccessMessage', message);
    },
    showInfoMessage({ commit }, message) {
      commit('clearMessages');
      commit('storeInfoMessage', message);
    },
    showWarningMessage({ commit }, message) {
      commit('clearMessages');
      commit('storeWarningMessage', message);
    },
    showError({ commit }, error) {
      commit('clearMessages');
      commit('storeError', error);
    },
    clearMessages({ commit }) {
      commit('clearMessages');
    },
    init({ commit }, router) {
      commit('setRouter', router);
    },
    announcementSeen({ commit }) {
      commit('announcementSeen', commit);
    },
  },
  getters: {
    userId(state) {
      return state.userId;
    },
    user(state) {
      return state.user;
    },
    userAvatar(state) {
      return state.avatar;
    },
    isAuthenticated(state) {
      return state.token !== null;
    },
    isPasswordExpired(state) {
      return state.passwordExpired;
    },
    hasAnyRole: state => (roles) => {
      if (!state.user) {
        return false;
      }
      if (!Array.isArray(roles)) {
        /* eslint-disable no-param-reassign */
        roles = [roles];
      }
      return roles.some(role => state.user.userPermissions.includes(role));
    },
    timezone(state) {
      return state.timezone || jstz.determine()
        .name();
    },
    lastActiveAt(state) {
      return state.lastActiveAt;
    },

    successMessage(state) {
      return state.successMessage;
    },
    successMessageTrigger(state) {
      return state.successMessageTrigger;
    },
    warningMessage(state) {
      return state.warningMessage;
    },
    warningMessageTrigger(state) {
      return state.warningMessageTrigger;
    },
    infoMessage(state) {
      return state.infoMessage;
    },
    infoMessageTrigger(state) {
      return state.infoMessageTrigger;
    },
    errorMessage(state) {
      return state.errorMessage;
    },
    errorMessageTrigger(state) {
      return state.errorMessageTrigger;
    },
    error(state) {
      return state.error;
    },
    validationErrors(state) {
      return state.validationErrors;
    },
    errorTrigger(state) {
      return state.errorTrigger;
    },
    announcementSeen(state) {
      return state.announcementSeen;
    },
  },
});

axios.interceptors.request.use((config) => {
  store.dispatch('updateLastActiveAt');
  return config;
});

export default store;



// WEBPACK FOOTER //
// ./src/store.js